/**
 * Tree-aware filtering and sorting for the Sources tab (LG-827), over the
 * same payload-generic forest the keyboard model consumes — so the rendered
 * rows, the nav rows, and the filter all see one structure.
 *
 * The predicates themselves come from the shared engines (`evalFilters`,
 * `applySorts`); this module only adds the tree shape: which subtrees survive
 * a filter, and how siblings reorder without ever leaving their parent.
 */

import type { LibraryItem } from './filter-engine'
import { applySorts, type SortSpec } from './sorting'
import type { TreeNavNode } from './source-tree-nav'

export interface PrunedForest<P> {
  forest: TreeNavNode<P>[]
  /**
   * Every node that must be open for some match to be visible: the strict
   * ancestors of each matching node. A match itself is never included — its
   * subtree is retained, not force-opened. Union these into the stored
   * open-set at render time; never persist them.
   */
  revealIds: ReadonlySet<string>
}

/**
 * Keep each node that matches (with its whole subtree — a matching chapter
 * keeps its sections reachable) or that has a matching descendant (pruned to
 * the branches that lead to matches). Everything else disappears.
 *
 * A node kept because it matches is returned by reference. That buys a
 * cheap `pruned !== input` identity check and less garbage per keystroke —
 * not row-render savings: the visible rows are rebuilt by
 * `flattenVisibleRows` whenever the forest or open-set changes, so row
 * memoization only pays off for focus moves (see TreeRow).
 */
export function pruneForest<P>(
  forest: readonly TreeNavNode<P>[],
  matches: (payload: P) => boolean,
): PrunedForest<P> {
  const revealIds = new Set<string>()
  const ancestors: string[] = []

  const visit = (node: TreeNavNode<P>): TreeNavNode<P> | null => {
    const selfMatch = matches(node.payload)
    if (selfMatch) for (const id of ancestors) revealIds.add(id)

    // Descend even under a matching node: a deeper match must still reveal
    // its own ancestor chain, or it would sit hidden inside the kept subtree.
    ancestors.push(node.id)
    const keptChildren: TreeNavNode<P>[] = []
    for (const child of node.children) {
      const kept = visit(child)
      if (kept !== null) keptChildren.push(kept)
    }
    ancestors.pop()

    if (selfMatch) return node
    if (keptChildren.length > 0) return { ...node, children: keptChildren }
    return null
  }

  const pruned: TreeNavNode<P>[] = []
  for (const root of forest) {
    const kept = visit(root)
    if (kept !== null) pruned.push(kept)
  }
  return { forest: pruned, revealIds }
}

/**
 * Reorder siblings within each parent — only ever within a parent, at every
 * depth, so the hierarchy itself never changes. No sort rules means the
 * authored order: the server's (order, createdAt, id) via mergeChildren,
 * returned untouched.
 *
 * Sorting reuses `applySorts` on each sibling group's LibraryItem
 * projections, so tree rows obey exactly the flat table's comparison rules —
 * null handling, tie stability, and all.
 */
export function sortSiblings<P>(
  forest: readonly TreeNavNode<P>[],
  sorts: SortSpec[],
  itemOf: (payload: P) => LibraryItem,
): readonly TreeNavNode<P>[] {
  if (sorts.length === 0) return forest

  const sortLevel = (nodes: readonly TreeNavNode<P>[]): TreeNavNode<P>[] => {
    const nodeOf = new Map<LibraryItem, TreeNavNode<P>>()
    const items = nodes.map((node) => {
      const item = itemOf(node.payload)
      nodeOf.set(item, node)
      return item
    })
    return applySorts(sorts, items).map((item) => {
      const node = nodeOf.get(item)
      // applySorts returns the same elements in a new array; an unknown
      // element can only mean that contract broke.
      if (node === undefined)
        throw new Error('applySorts returned a foreign item')
      return { ...node, children: sortLevel(node.children) }
    })
  }

  return sortLevel(forest)
}
